import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Mic,
  AlignLeft,
  Languages,
  FileText,
  HardDrive,
  XCircle
} from 'lucide-react';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { useGetAdminOrganizationUsage } from '@utils/api/admin/use-get-admin-organization-usage';

type OrganizationUsageTabProps = {
  organizationId: string;
};

/**
 * Usage tab for organization details
 * Displays STT minutes, alignment minutes, translation characters,
 * session count, and storage documents in a list format.
 */
export default function OrganizationUsageTab({
  organizationId
}: OrganizationUsageTabProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data, isLoading, isError, refetch } =
    useGetAdminOrganizationUsage(organizationId);

  const usage = data?.result;

  // Helper to format numbers with locale
  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  // Helper to format minutes
  const formatMinutes = (minutes: number | undefined): string => {
    if (minutes === undefined || minutes === null) return '0';
    if (minutes < 1) return '<1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  if (isLoading) {
    return <Skeleton variant="table" rows={5} className="mt-2" />;
  }

  if (isError) {
    return (
      <div
        className="rounded-2xl border p-8 text-center"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('secondLayer')
        }}
      >
        <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500 dark:text-red-400" />
        <p className="text-sm mb-4" style={{ color: getColour('normalText') }}>
          {t('failed-to-load-usage')}
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-2xl text-sm font-medium"
          style={{
            backgroundColor: getColour('thirdLayer'),
            color: getColour('normalText')
          }}
        >
          {t('retry')}
        </button>
      </div>
    );
  }

  if (!usage) {
    return (
      <div
        className="rounded-2xl border p-8 text-center"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('secondLayer')
        }}
      >
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('no-usage-data')}
        </p>
      </div>
    );
  }

  const usageItems = [
    {
      label: t('stt-minutes'),
      value: formatMinutes(usage.stt_minutes),
      icon: Mic,
      iconClass: 'text-blue-500',
      bgClass: 'bg-blue-500/10'
    },
    {
      label: t('alignment-minutes'),
      value: formatMinutes(usage.alignment_minutes),
      icon: AlignLeft,
      iconClass: 'text-violet-500',
      bgClass: 'bg-violet-500/10'
    },
    {
      label: t('translation-characters'),
      value: formatNumber(usage.translation_characters),
      icon: Languages,
      iconClass: 'text-emerald-500',
      bgClass: 'bg-emerald-500/10'
    },
    {
      label: t('sessions'),
      value: formatNumber(usage.session_count),
      icon: FileText,
      iconClass: 'text-amber-500',
      bgClass: 'bg-amber-500/10'
    },
    {
      label: t('storage-documents'),
      value: formatNumber(usage.storage_documents),
      icon: HardDrive,
      iconClass: 'text-indigo-500',
      bgClass: 'bg-indigo-500/10'
    }
  ];

  return (
    <div>
      {/* Usage List */}
      <div
        className="rounded-2xl border divide-y"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('secondLayer')
        }}
      >
        {usageItems.map(item => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className="flex items-center justify-between px-4 py-3"
              style={{
                borderColor: getColour('border')
              }}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-2xl ${item.bgClass}`}>
                  <Icon className={`h-4 w-4 ${item.iconClass}`} />
                </div>
                <span
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {item.label}
                </span>
              </div>
              <span
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {item.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
